// 3rd party imports
import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// ui
import { UitkCard } from 'uitk-react-cards';
import { UitkTextInput, UitkFakeInput } from 'uitk-react-input';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLoader } from 'uitk-react-loader';
import { UitkPrimaryButton, UitkButtonSize, UitkSecondaryButton } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';

import { CopyButton } from './copy-button';

// hooks
import { useUrlValidation } from '../../hooks/useUrlValidation';

// models
import { Program } from 'models';

interface CustomQuicklinkProps {
  l10n?: ILocalization;
  camRef?: string;
  creativeRef?: string;
  program?: Program;
};

export const CustomQuicklink: React.FunctionComponent<CustomQuicklinkProps> = ({
  l10n,
  camRef,
  creativeRef,
  program,
}) => {
  const { title, validUrlPattern } = program;

  const [status, setStatus] = React.useState(null);
  const [url, setUrl] = React.useState({ urlValue: '' });
  const [quicklinkUrl, setQuicklinkUrl] = React.useState('');
  const [quicklinkCode, setQuicklinkCode] = React.useState('');
  const [copyUrlSuccess, setCopyUrlSuccess] = React.useState(null);
  const [copyCodeSuccess, setCopyCodeSuccess] = React.useState(null);

  const { control, errors, handleSubmit } = useForm({ mode: 'onBlur' });

  // labels
  // static label - need to update to localization
  // TODO: re-order the names for titles as Vrbo UK instead of UK Vrbo https://jira.expedia.biz/browse/RPP-107
  const urlLabel = `Enter a ${title} URL`;
  const urlMissingErrorLabel = 'URL missing';
  const urlInvalidErrorLabel = `URL invalid: must be a ${title} URL`;
  const urlSubmit = 'Submit';
  const urlUpdate = 'Update';
  const quicklinkHeading = 'Create a custom tracking link';
  const quicklinkSubHeading = 'Here is your custom link and code:';
  const quicklinkUrlLabel = 'Link';
  const quicklinkCodeLabel = 'Code';
  const quicklinkCopyLabel = 'Copy';
  const quicklinkCopiedLabel = 'Copied';

  const [hasUrl] = useUrlValidation(
    {
      urlValue: url.urlValue,
    },
    validUrlPattern,
  );

  /* istanbul ignore next */
  const handleClear = () => {
    setUrl({ urlValue: '' });
  };


  const generateQuickLink = (inputUrl) => {
    if (camRef) {
      const camref = `/camref:${camRef}`;
      const creativeref = creativeRef ? `/creativeref:${creativeRef}` : '';
      const destination = `/destination:${inputUrl}`;

      return `https://vrbo.prf.hn/click${camref}${creativeref}${destination}`;
    } else {
      return inputUrl;
    }
  };

  const onSubmit = handleSubmit(({ url: submittedURL }) => {
    setStatus('LOADING');

    triggerQuicklinkBuild(submittedURL);
  });

  const triggerQuicklinkBuild = (submittedUrl) => {
    submittedUrl = submittedUrl.replace(/\s+/g, '');
    const urlPattern = new RegExp(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/);

    if (urlPattern.test(submittedUrl)) {
      submittedUrl = submittedUrl.replace('https://', '').replace('http://', '').replace('www.', '');
      submittedUrl = `https://www.${submittedUrl}`;
    }

    // Dynamic link param will be here
    const newQuicklinkUrl = generateQuickLink(submittedUrl);
    const newQuicklinkCode = `<a href="${newQuicklinkUrl}">Find your dream vacation rental on Vrbo</a>`;

    setUrl({ urlValue: submittedUrl });
    setQuicklinkUrl(newQuicklinkUrl);
    setQuicklinkCode(newQuicklinkCode);

    setStatus(null);
  };

  const urlValidation = () => {
    if (!hasUrl) {
      return hasUrl || urlInvalidErrorLabel;
    }
  };

  const setTimer = () => {
    setTimeout(() => {
      setCopyCodeSuccess(null);
      setCopyUrlSuccess(null);
    }, 2000);
    clearTimeout();
  };

  const copyBtnOnClick = (copy) => {
    navigator.clipboard.writeText(copy);

    if (copy === quicklinkUrl) {
      setCopyUrlSuccess('copied');
      setTimer();
    } else if (copy === quicklinkCode) {
      setCopyCodeSuccess('copied');
      setTimer();
    }
  };

  return (
    <>
      <UitkSpacing margin={{ blockend: 'one' }}>
        <UitkHeading tag="h2" size={4}>
          <LocalizedText message="products.quicklink.custom.title" />
        </UitkHeading>
      </UitkSpacing>
      <UitkSpacing margin={{ blockend: 'six' }}>
        <UitkText>
          <LocalizedText message="products.quicklink.custom.content" />
        </UitkText>
      </UitkSpacing>
      <UitkSpacing margin={{ blockend: 'six' }}>
        <UitkCard padded={true}>
          <form name={'CustomQuicklink'} onSubmit={onSubmit} method="POST">
            <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
              <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
                <UitkSpacing padding={{ blockstart: 'three' }}>
                  <UitkText>{urlLabel}</UitkText>
                </UitkSpacing>
              </UitkLayoutGridItem>
              <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
                <UitkLayoutFlex space="two" className="QuicklinkInputs">
                  <UitkLayoutFlexItem grow={1}>
                    <UitkLayoutFlex direction="column">
                      <Controller
                        control={control}
                        name="url"
                        defaultValue=""
                        type="url"
                        rules={{
                          required: urlMissingErrorLabel,
                          validate: urlValidation,
                        }}
                        render={({ onChange, onBlur, value, name }) => (
                          <UitkTextInput
                            id="CustomQuicklinkUrlInput"
                            placeholder="URL"
                            name={name}
                            label="url"
                            value={url.urlValue}
                            onChange={({ target: { value: newValue } }) => {
                              setUrl({ ...url, urlValue: newValue });
                              onChange(newValue);
                            }}
                            onBlur={onBlur}
                            hasVisibleLabel={false}
                            handleClear={handleClear}
                            hasClearButton={true}
                            data-cy="quicklink-url-field"
                            invalid={errors.url?.message}
                          />
                        )}
                      />
                    </UitkLayoutFlex>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem alignSelf="start">
                    <UitkPrimaryButton
                      id="CustomQuicklinkSubmitButton"
                      type="submit"
                      className="QuicklinkButton"
                      disabled={errors.length > 0 || status === 'LOADING'}
                      size={UitkButtonSize.LARGE}
                      data-cy="quicklink-submit"
                    >
                      {status === 'LOADING' ? '' : quicklinkUrl ? urlUpdate : urlSubmit}
                      {status === 'LOADING' && <UitkLoader isSmall={true} isDarkBackground={true} />}
                    </UitkPrimaryButton>
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
              </UitkLayoutGridItem>
            </UitkLayoutGrid>
            {quicklinkUrl && (
              <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
                <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
                  <div />
                </UitkLayoutGridItem>
                <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
                  <div>
                    <div>
                      <UitkSpacing margin={{ block: 'four' }}>
                        <UitkHeading tag="h4" size={6}>
                          {quicklinkSubHeading}
                        </UitkHeading>
                      </UitkSpacing>
                    </div>
                    <UitkLayoutGrid columns={{ small: 1, large: 2 }} space="four">
                      <UitkLayoutGridItem>
                        <UitkLayoutFlex space="two" className="QuicklinkInputs">
                          <UitkFakeInput
                            id="CustomQuicklinkUrl"
                            label={quicklinkUrlLabel}
                            value={quicklinkUrl}
                            data-cy="quicklink-url-field"
                          />
                          <CopyButton value={quicklinkUrl} />
                        </UitkLayoutFlex>
                      </UitkLayoutGridItem>
                      <UitkLayoutGridItem>
                        <UitkLayoutFlex space="two" className="QuicklinkInputs">
                          <UitkFakeInput
                            id="CustomQuicklinkCode"
                            label={quicklinkCodeLabel}
                            value={quicklinkCode}
                            data-cy="quicklink-url-field"
                          />
                          <CopyButton value={quicklinkCode} />
                        </UitkLayoutFlex>
                      </UitkLayoutGridItem>
                    </UitkLayoutGrid>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            )}
          </form>
        </UitkCard>
      </UitkSpacing>
    </>
  );
};
